import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, MapPin, Package, Phone, LogOut, CreditCard, 
  ChevronRight, Heart, Shield, Bell, HelpCircle, 
  Settings, Plus, Trash2, Star, Edit2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWishlist } from '@/context/WishlistContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, loginWithGoogle } = useAuth();
  const { wishlistCount } = useWishlist();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savedCards, setSavedCards] = useState([
    // Demo saved cards - in production these would come from backend/Stripe
  ]);
  const [newCard, setNewCard] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: ''
  });

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const handleAddCard = () => {
    // Validate card
    if (!newCard.number || !newCard.expiry || !newCard.cvv || !newCard.name) {
      toast.error('Please fill all card details');
      return;
    }
    
    // In production, this would call Stripe API to tokenize and save the card
    const maskedNumber = `•••• •••• •••• ${newCard.number.slice(-4)}`;
    setSavedCards([...savedCards, {
      id: Date.now(),
      last4: newCard.number.slice(-4),
      brand: newCard.number.startsWith('4') ? 'Visa' : 'Mastercard',
      expiry: newCard.expiry,
      isDefault: savedCards.length === 0
    }]);
    
    setNewCard({ number: '', expiry: '', cvv: '', name: '' });
    setShowPaymentModal(false);
    toast.success('Card added successfully');
  };

  const handleDeleteCard = (cardId) => {
    setSavedCards(savedCards.filter(c => c.id !== cardId));
    toast.success('Card removed');
  };

  const handleSetDefault = (cardId) => {
    setSavedCards(savedCards.map(c => ({
      ...c,
      isDefault: c.id === cardId
    })));
    toast.success('Default card updated');
  };

  // Menu items for authenticated users
  const menuItems = [
    {
      id: 'profile',
      label: 'My Profile',
      description: 'View and edit your profile',
      icon: User,
      path: '/profile',
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      id: 'orders',
      label: 'My Orders',
      description: 'Track and manage orders',
      icon: Package,
      path: '/my-orders',
      color: 'text-orange-600',
      bg: 'bg-orange-50'
    },
    {
      id: 'wishlist',
      label: 'Wishlist',
      description: `${wishlistCount} saved items`,
      icon: Heart,
      path: '/wishlist',
      color: 'text-red-500',
      bg: 'bg-red-50',
      badge: wishlistCount
    },
    {
      id: 'addresses',
      label: 'Saved Addresses',
      description: 'Manage delivery addresses',
      icon: MapPin,
      path: '/profile?tab=addresses',
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      id: 'payments',
      label: 'Payment Methods',
      description: 'Manage saved cards',
      icon: CreditCard,
      action: () => setShowPaymentModal(true),
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      id: 'contact',
      label: 'Contact Us',
      description: 'Get help and support',
      icon: Phone,
      path: '/contact',
      color: 'text-teal-600',
      bg: 'bg-teal-50'
    }
  ];

  // Not logged in view
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0047AB] to-[#0066FF] px-4 py-8 text-white">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-xl font-bold">Welcome to DXB2BOM</h1>
            <p className="text-white/80 text-sm mt-1">Sign in to access your account</p>
          </div>
        </div>

        {/* Login Options */}
        <div className="p-4 -mt-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <Button
              onClick={loginWithGoogle}
              className="w-full h-12 bg-[#0047AB] hover:bg-[#003380] text-white font-semibold mb-3"
              data-testid="google-login-btn"
            >
              <img 
                src="https://www.google.com/favicon.ico" 
                alt="Google" 
                className="w-5 h-5 mr-2"
              />
              Continue with Google
            </Button>
            
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">or</span>
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full h-12 border-2 font-semibold"
              data-testid="email-login-btn"
            >
              Sign in with Email
            </Button>
          </div>

          {/* Benefits */}
          <div className="mt-6 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900 px-1">Why create an account?</h3>
            {[
              { icon: Package, text: 'Track your orders in real-time' },
              { icon: Heart, text: 'Save items to your wishlist' },
              { icon: MapPin, text: 'Save addresses for faster checkout' },
              { icon: CreditCard, text: 'Securely save payment methods' }
            ].map((benefit, i) => (
              <div key={`benefit-${benefit.text}`} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-slate-100">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-[#0047AB]" />
                </div>
                <span className="text-sm text-slate-700">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Logged in view
  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Profile Header */}
      <div className="bg-gradient-to-br from-[#0047AB] to-[#0066FF] px-4 py-6 text-white">
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-16 h-16 rounded-full border-2 border-white/30"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate">{user?.name || 'User'}</h1>
            <p className="text-white/80 text-sm truncate">{user?.email}</p>
          </div>
          <Link to="/profile">
            <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
              <Edit2 className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Menu Items */}
      <div className="p-4 space-y-2">
        {menuItems.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            {item.path ? (
              <Link
                to={item.path}
                className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all active:scale-[0.99]"
                data-testid={`menu-${item.id}`}
              >
                <div className={`w-11 h-11 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.description}</p>
                </div>
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </Link>
            ) : (
              <button
                onClick={item.action}
                className="w-full flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all active:scale-[0.99]"
                data-testid={`menu-${item.id}`}
              >
                <div className={`w-11 h-11 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-semibold text-slate-900">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            )}
          </motion.div>
        ))}

        {/* Logout Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: menuItems.length * 0.05 }}
          onClick={handleLogout}
          className="w-full flex items-center gap-3 bg-white rounded-xl p-4 border border-red-100 hover:border-red-200 hover:bg-red-50 transition-all mt-4"
          data-testid="logout-btn"
        >
          <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <LogOut className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="font-semibold text-red-600">Sign Out</p>
            <p className="text-xs text-slate-500">Log out of your account</p>
          </div>
        </motion.button>
      </div>

      {/* Payment Methods Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#0047AB]" />
              Payment Methods
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Saved Cards */}
            {savedCards.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Saved Cards</p>
                {savedCards.map((card) => (
                  <div 
                    key={card.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${card.isDefault ? 'border-[#0047AB] bg-blue-50' : 'border-slate-200'}`}
                  >
                    <div className="w-10 h-7 bg-gradient-to-r from-slate-700 to-slate-900 rounded flex items-center justify-center">
                      <span className="text-white text-[8px] font-bold">{card.brand}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">•••• {card.last4}</p>
                      <p className="text-xs text-slate-500">Expires {card.expiry}</p>
                    </div>
                    {card.isDefault && (
                      <span className="text-[10px] bg-[#0047AB] text-white px-2 py-0.5 rounded font-medium">
                        Default
                      </span>
                    )}
                    <div className="flex gap-1">
                      {!card.isDefault && (
                        <button
                          onClick={() => handleSetDefault(card.id)}
                          className="p-1.5 text-slate-400 hover:text-[#0047AB]"
                          title="Set as default"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500"
                        title="Remove card"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-slate-50 rounded-lg">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No saved cards</p>
                <p className="text-slate-400 text-xs">Add a card for faster checkout</p>
              </div>
            )}

            {/* Add New Card Form */}
            <div className="border-t border-slate-200 pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Add New Card</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Card Number</Label>
                  <Input
                    placeholder="1234 5678 9012 3456"
                    value={newCard.number}
                    onChange={(e) => setNewCard({ ...newCard, number: e.target.value.replace(/\D/g, '').slice(0, 16) })}
                    maxLength={16}
                    data-testid="card-number-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Expiry (MM/YY)</Label>
                    <Input
                      placeholder="MM/YY"
                      value={newCard.expiry}
                      onChange={(e) => {
                        let val = e.target.value.replace(/\D/g, '');
                        if (val.length >= 2) val = val.slice(0, 2) + '/' + val.slice(2, 4);
                        setNewCard({ ...newCard, expiry: val });
                      }}
                      maxLength={5}
                      data-testid="card-expiry-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CVV</Label>
                    <Input
                      type="password"
                      placeholder="•••"
                      value={newCard.cvv}
                      onChange={(e) => setNewCard({ ...newCard, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      maxLength={4}
                      data-testid="card-cvv-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Cardholder Name</Label>
                  <Input
                    placeholder="Name on card"
                    value={newCard.name}
                    onChange={(e) => setNewCard({ ...newCard, name: e.target.value })}
                    data-testid="card-name-input"
                  />
                </div>
                <Button
                  onClick={handleAddCard}
                  className="w-full bg-[#0047AB] hover:bg-[#003380]"
                  data-testid="add-card-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Card
                </Button>
              </div>
              
              <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                Your card details are encrypted and secure
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountPage;
